import React from 'react';

import Link from '@atlaskit/link';
import { AtlassianIcon, ConfluenceIcon, JiraIcon } from '@atlaskit/logo';
import { Inline, Text, xcss } from '@atlaskit/primitives';
import { type AgentKnowledgeSource } from '@atlassian/conversation-assistant-service';

import { getAtlasHost } from '../../utils/atlas-url';

import { GoogleDriveIcon } from './google-drive-icon';
import { MicrosoftSharepointIcon } from './microsoft-sharepoint-icon';
import { SlackIcon } from './slack-icon';

type SourceNames = AgentKnowledgeSource['source'];

const getIconForSource = (source: SourceNames) => {
	switch (source) {
		case 'confluence':
			return <ConfluenceIcon size="small" appearance="brand" />;
		case 'jira':
			return <JiraIcon size="small" appearance="brand" />;
		case 'atlas':
			return <AtlassianIcon size="small" appearance="brand" />;
		case 'microsoft_sharepoint':
			return <MicrosoftSharepointIcon size="medium" label="" />;
		case 'slack':
			return <SlackIcon size="medium" label="" />;
		case 'google_drive':
			return <GoogleDriveIcon size="medium" label="" />;
		default:
			return null;
	}
};

const getLabelForSource = (source: SourceNames) => {
	switch (source) {
		case 'confluence':
			return 'Confluence';
		case 'jira':
			return 'Jira';
		case 'atlas':
			return 'Atlassian Home';
		case 'microsoft_sharepoint':
			return 'Microsoft Sharepoint';
		case 'slack':
			return 'Slack';
		case 'google_drive':
			return 'Google Drive';
		default:
			return '';
	}
};

const wrapperStyles = xcss({
	height: '20px',
});

const KnowledgeSourceProduct = ({
	source,
	cloudUrl,
}: {
	source: SourceNames;
	cloudUrl?: string;
}) => {
	const LinkOrText = cloudUrl ? Link : Text;

	const getLabelRender = () => {
		switch (source) {
			case 'jira':
				return (
					<LinkOrText href={`${cloudUrl}/jira`} target="_blank">
						{getLabelForSource(source)}
					</LinkOrText>
				);
			case 'confluence':
				return (
					<LinkOrText href={`${cloudUrl}/wiki`} target="_blank">
						{getLabelForSource(source)}
					</LinkOrText>
				);
			case 'atlas':
				return (
					<Link href={getAtlasHost()} target="_blank">
						{getLabelForSource(source)}
					</Link>
				);
			default:
				return <Text>{getLabelForSource(source)}</Text>;
		}
	};

	return (
		<Inline xcss={wrapperStyles} alignBlock="center" space="space.075">
			{getIconForSource(source)}
			{getLabelRender()}
		</Inline>
	);
};

export const getKnowledgeProductsRender = ({
	sources,
	cloudUrl,
}: {
	sources: Array<AgentKnowledgeSource>;
	cloudUrl?: string;
}): React.ReactNode[] | null => {
	const knowledgeSourcesRender: React.ReactNode[] = [];

	sources.forEach((source) => {
		if (!source.enabled) {
			return null;
		}

		// if confluence filters has any specific pages or space filter
		// we don't render the confluence product
		if (
			source.source === 'confluence' &&
			(source.configuration?.parent_filter?.length || source.configuration?.space_filter?.length)
		) {
			return null;
		}

		// if jira filters has any specific project filter
		// we don't render the jira product
		if (source.source === 'jira' && source.configuration?.project_filter?.length) {
			return null;
		}

		knowledgeSourcesRender.push(
			<KnowledgeSourceProduct key={source.source} source={source.source} cloudUrl={cloudUrl} />,
		);
	});

	return knowledgeSourcesRender.length ? knowledgeSourcesRender : null;
};
